import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import { Input, Modal, Select, Table, Spin, message } from "antd"
import { DownOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { ReferralsApi } from "../../Apis/referralsApi"
import { translations } from "../../utils/Translation/staticTranslation"

const PAGE_SIZE = 10

const formatRewardAmount = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value)
  if (!Number.isFinite(n)) return "SAR 0.00"
  const text = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `SAR ${text}`
}

const formatStatusDisplay = (status) => {
  const s = String(status || "").trim().toLowerCase().replace(/_/g, " ")
  if (!s) return "—"
  return s.replace(/\b\w/g, (c) => c.toUpperCase())
}

const isArabicLang = (lang) =>
  String(lang || "").toLowerCase().startsWith("ar")

const rewardRowStatusDisplay = (statusRaw, lang, t) => {
  const normalized = String(statusRaw || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
  if (isArabicLang(lang)) {
    const map = {
      credit: t.status_credit,
      expired: t.status_expired,
      hold: t.status_hold,
      pending: t.status_pending,
      redeemed: t.status_redeemed,
      "under review": t.status_under_review,
    }
    if (map[normalized]) return map[normalized]
  }
  return formatStatusDisplay(statusRaw)
}

/** Backend may return `details` as a string like "{'error': '...'}" */
const parseDetailsError = (details) => {
  if (details == null) return null
  if (typeof details === "object" && details.error != null) {
    return String(details.error).trim() || null
  }
  if (typeof details !== "string") return null
  const trimmed = details.trim()
  const m = trimmed.match(/['"]error['"]\s*:\s*['"]([^'"]+)['"]/)
  if (m) return m[1].trim()
  return trimmed || null
}

const rewardApiErrorMessage = (data) => {
  if (!data || typeof data !== "object") return null
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim()
  if (typeof data.error === "string" && data.error.trim()) return data.error.trim()
  return parseDetailsError(data.details)
}

const mapApiRewardRow = (row, lang, t) => {
  const id = row.id
  const statusRaw = (row.status || "").trim().toLowerCase()
  const statusLabelEn = (row.status_label || row.status || "—").trim()
  const statusLabelAr = String(row.status_label_arabic || "").trim()
  const useAr = isArabicLang(lang)
  const statusLabel = useAr
    ? statusLabelAr || statusLabelEn
    : statusLabelEn
  const statusDisplay = useAr
    ? statusLabelAr || rewardRowStatusDisplay(statusRaw, lang, t)
    : rewardRowStatusDisplay(statusRaw, lang, t)
  return {
    key: id,
    id,
    rewardId: (row.reward_id || "").trim() || `RW${String(id).padStart(3, "0")}`,
    user: row.user_name?.trim() || "—",
    email: (row.user_email || "").trim(),
    referrerName: row.referrer_name?.trim() || "—",
    referrerEmail: (row.referrer_email || "").trim(),
    amount: formatRewardAmount(row.amount),
    status: statusRaw,
    statusLabel,
    statusDisplay,
    date: (row.earned_date || "—").trim(),
    canApprove: Boolean(row.can_approve),
  }
}

const pinReferralModalCloseToLeft = (modalClassPrefix, topPx, leftPx) => {
  const raw = String(localStorage.getItem("language") || "ar").toLowerCase()
  if (raw !== "ar" && !raw.startsWith("ar-")) return
  const modalEl = document.querySelector(`.${modalClassPrefix}.ant-modal`)
  const btn = modalEl?.querySelector(".ant-modal-close")
  if (!btn) return
  btn.style.setProperty("top", `${topPx}px`, "important")
  btn.style.setProperty("left", `${leftPx}px`, "important")
  btn.style.setProperty("right", "auto", "important")
  btn.style.setProperty("inset-inline-start", `${leftPx}px`, "important")
  btn.style.setProperty("inset-inline-end", "auto", "important")
}

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (
    normalized === "redeemed" ||
    normalized === "claimed" ||
    normalized === "success"
  )
    return "status-success"
  if (
    normalized === "pending" ||
    normalized === "hold" ||
    normalized === "under_review"
  )
    return "status-pending"
  if (normalized === "expired") return "status-expired"
  if (normalized === "credit" || normalized === "unclaimed")
    return "status-unclaimed"
  if (normalized === "failed") return "status-failed"
  return "status-default"
}

const RewardsTab = ({ readOnly = false }) => {
  const lang = localStorage.getItem("language") || "ar"
  const isArabicLayout = isArabicLang(lang)
  const t = translations[lang]?.referralsPage || translations.en.referralsPage

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedReward, setSelectedReward] = useState(null)
  const [approveSubmitting, setApproveSubmitting] = useState(false)
  const [revokeSubmitting, setRevokeSubmitting] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  const pinRewardModalClose = useCallback(() => {
    pinReferralModalCloseToLeft("ref-reward-details-modal", 18, 20)
  }, [])

  useLayoutEffect(() => {
    if (!isDetailsOpen) return
    pinRewardModalClose()
    const raf = requestAnimationFrame(pinRewardModalClose)
    const t0 = window.setTimeout(pinRewardModalClose, 0)
    const t1 = window.setTimeout(pinRewardModalClose, 120)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(t0)
      window.clearTimeout(t1)
    }
  }, [isDetailsOpen, selectedReward, pinRewardModalClose])

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = search.trim()
        if (prev !== next) setPage(1)
        return next
      })
    }, 350)
    return () => clearTimeout(t)
  }, [search])

  const loadRewards = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ReferralsApi.getRewardsManagement({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      const payload = res.data
      const result = payload?.result
      if (payload?.success && result && Array.isArray(result.results)) {
        setRows(result.results.map((row) => mapApiRewardRow(row, lang, t)))
        setTotal(typeof result.count === "number" ? result.count : result.results.length)
      } else {
        setRows([])
        setTotal(0)
        message.error("Could not load rewards.")
      }
    } catch (e) {
      setRows([])
      setTotal(0)
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to load rewards."
      )
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, statusFilter, lang, t])

  useEffect(() => {
    loadRewards()
  }, [loadRewards])

  const handleExportCsv = useCallback(async () => {
    if (readOnly) return
    setExportLoading(true)
    try {
      const res = await ReferralsApi.exportRewardsManagementCsv({
        search: debouncedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      const blob = res?.data
      if (!(blob instanceof Blob)) {
        message.error("Could not download export.")
        return
      }
      const cd = res.headers?.["content-disposition"]
      const match = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const fallback = `referral-rewards-${dayjs().format("YYYY-MM-DD")}.csv`
      const filename = match?.[1]?.trim() || fallback
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success("Export downloaded.")
    } catch (e) {
      const errBody = e?.response?.data
      if (errBody instanceof Blob) {
        try {
          const text = await errBody.text()
          const j = JSON.parse(text)
          message.error(
            j.message || j.error || j.detail || "Failed to export rewards.",
          )
        } catch {
          message.error("Failed to export rewards.")
        }
      } else {
        message.error(
          errBody?.message ||
            errBody?.error ||
            e?.message ||
            "Failed to export rewards.",
        )
      }
    } finally {
      setExportLoading(false)
    }
  }, [debouncedSearch, statusFilter, readOnly])

  const handleApproveReward = useCallback(async () => {
    if (readOnly) return
    if (selectedReward?.id == null || selectedReward?.status === "credit") return
    setApproveSubmitting(true)
    try {
      const res = await ReferralsApi.patchReferralReward(selectedReward.id, {
        action: "approve",
      })
      const payload = res?.data
      if (payload?.success !== false) {
        message.success("Reward approved.")
        setIsDetailsOpen(false)
        setSelectedReward(null)
        await loadRewards()
      } else {
        message.error(
          rewardApiErrorMessage(payload) || payload?.message || "Could not approve reward."
        )
      }
    } catch (e) {
      const errData = e?.response?.data
      message.error(
        rewardApiErrorMessage(errData) ||
          errData?.message ||
          errData?.error ||
          e?.message ||
          "Failed to approve reward."
      )
    } finally {
      setApproveSubmitting(false)
    }
  }, [selectedReward, loadRewards, readOnly])

  const handleRevokeReward = useCallback(async () => {
    if (readOnly) return
    if (selectedReward?.id == null) return
    if (selectedReward?.status === "credit") return
    setRevokeSubmitting(true)
    try {
      const res = await ReferralsApi.patchReferralReward(selectedReward.id, {
        action: "revoke",
      })
      const payload = res?.data
      if (payload?.success !== false) {
        message.success("Reward revoked.")
        setIsDetailsOpen(false)
        setSelectedReward(null)
        await loadRewards()
      } else {
        message.error(
          rewardApiErrorMessage(payload) || payload?.message || "Could not revoke reward."
        )
      }
    } catch (e) {
      const errData = e?.response?.data
      message.error(
        rewardApiErrorMessage(errData) ||
          errData?.message ||
          errData?.error ||
          e?.message ||
          "Failed to revoke reward."
      )
    } finally {
      setRevokeSubmitting(false)
    }
  }, [selectedReward, loadRewards, readOnly])

  const rewardsColumns = useMemo(
    () => [
      {
        title: t.col_user,
        dataIndex: "user",
        key: "user",
        render: (_, row) => (
          <div className="ref-reward-user-cell">
            <div className={`ref-reward-avatar avatar-${Number(row.id) % 6}`}>
              {(row.user || "—").slice(0, 2).toUpperCase()}
            </div>
            <div className="ref-reward-user-info">
              <div className="ref-reward-user-name">{row.user}</div>
              <div className="ref-reward-user-email">{row.email}</div>
            </div>
          </div>
        ),
        width: 400,
      },
      { title: t.col_amount, dataIndex: "amount", key: "amount" },
      {
        title: t.status,
        dataIndex: "statusDisplay",
        key: "status",
        render: (_, row) => (
          <span
            className={`ref-status-pill ${statusClass(row.status)}`}
            title={row.statusLabel}
          >
            {row.statusDisplay}
          </span>
        ),
      },
      { title: t.col_earned_date, dataIndex: "date", key: "date" },
      {
        title: t.col_actions,
        key: "actions",
        align: "right",
        width: 90,
        render: (_, row) => (
          <button
            type="button"
            className="ref-reward-action-btn"
            onClick={() => {
              setSelectedReward(row)
              setIsDetailsOpen(true)
            }}
          >
            <img
              src="/store/carbon_view.svg"
              alt="View actions"
              className="ref-reward-action-icon"
            />
          </button>
        ),
      },
    ],
    [t, readOnly],
  )

  const rewardStatusFilterOptions = useMemo(
    () => [
      { label: t.filter_all_status, value: "all" },
      { label: t.status_credit, value: "credit" },
      { label: t.status_expired, value: "expired" },
      { label: t.status_hold, value: "hold" },
      { label: t.status_pending, value: "pending" },
      { label: t.status_redeemed, value: "redeemed" },
      { label: t.status_under_review, value: "under_review" },
    ],
    [t],
  )

  return (
    <div className="referrals-simple-view">
      <div className="referrals-subheader">
        <h2>{t.rewards_management}</h2>
        <button
          className="ref-button-primary ref-reward-export-btn"
          type="button"
          disabled={readOnly || exportLoading}
          title={readOnly ? t.read_only_hint : undefined}
          onClick={handleExportCsv}
        >
          {exportLoading ? t.exporting : t.export}
          <DownOutlined />
        </button>
      </div>
      <div className="ref-panel ref-reward-table-panel">
        <div className="ref-reward-filters-row">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search_rewards}
            className="ref-reward-search-input"
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
            className="ref-reward-status-filter"
            options={rewardStatusFilterOptions}
          />
        </div>
        <Spin spinning={loading}>
          <Table
            className="ref-ant-table ref-reward-table"
            columns={rewardsColumns}
            rowSelection={readOnly ? undefined : { columnWidth: 38 }}
            dataSource={rows}
            pagination={{
              current: page,
              total,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              showLessItems: false,
              position: ["bottomRight"],
              itemRender: (pageNumber, type, originalElement) => {
                if (type === "prev")
                  return (
                    <span className="ref-reward-pagination-link">{t.pagination_prev}</span>
                  )
                if (type === "next")
                  return (
                    <span className="ref-reward-pagination-link">{t.pagination_next}</span>
                  )
                if (type === "jump-prev" || type === "jump-next")
                  return (
                    <span className="ref-reward-pagination-ellipsis">...</span>
                  )
                if (type === "page") {
                  const isActive = pageNumber === page
                  return (
                    <span
                      className={`ref-reward-pagination-page ${isActive ? "active" : ""}`}
                    >
                      {pageNumber}
                    </span>
                  )
                }
                return originalElement
              },
              onChange: (nextPage) => setPage(nextPage),
            }}
            size="middle"
          />
        </Spin>
      </div>
      <Modal
        open={isDetailsOpen}
        onCancel={() => setIsDetailsOpen(false)}
        footer={null}
        width={690}
        className={`ref-reward-details-modal${isArabicLayout ? " ref-reward-details-modal--ar" : ""}`}
        closeIcon={<span className="ref-reward-details-close">×</span>}
        afterOpenChange={(open) => {
          if (open) {
            pinRewardModalClose()
            window.setTimeout(pinRewardModalClose, 0)
            window.setTimeout(pinRewardModalClose, 200)
          }
        }}
      >
        <div className="ref-reward-details-title">{t.reward_details}</div>
        <div className="ref-reward-details-grid">
          <div className="ref-reward-details-row">
            <span>{t.reward_id}</span>
            <span>{selectedReward?.rewardId || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.user_name}</span>
            <span>{selectedReward?.user || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.user_email}</span>
            <span>{selectedReward?.email || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.referrer_name}</span>
            <span>{selectedReward?.referrerName || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.referrer_email}</span>
            <span>{selectedReward?.referrerEmail || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.reward_value}</span>
            <span>{selectedReward?.amount || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.status}</span>
            <span>{selectedReward?.statusLabel || "-"}</span>
          </div>
          <div className="ref-reward-details-row">
            <span>{t.col_earned_date}</span>
            <span>{selectedReward?.date || "-"}</span>
          </div>
        </div>
        <div
          className={`ref-reward-details-actions${isArabicLayout ? " ref-reward-details-actions--ar" : ""}`}
        >
          <div className="ref-reward-details-actions-primary">
            <button
              type="button"
              className="ref-reward-approve-btn"
              disabled={
                readOnly ||
                selectedReward?.id == null ||
                approveSubmitting ||
                revokeSubmitting ||
                selectedReward?.status === "credit"
              }
              onClick={handleApproveReward}
              title={
                readOnly
                  ? t.read_only_hint
                  : selectedReward?.status === "credit"
                    ? t.reward_credited_no_mutations_hint
                    : selectedReward?.canApprove === false
                      ? "Backend may reject if this reward is not approvable."
                      : undefined
              }
            >
              {approveSubmitting ? t.approving : t.approve_reward}
            </button>
            <button
              type="button"
              className="ref-reward-revoke-btn"
              disabled={
                readOnly ||
                selectedReward?.id == null ||
                approveSubmitting ||
                revokeSubmitting ||
                selectedReward?.status === "credit"
              }
              title={
                readOnly
                  ? t.read_only_hint
                  : selectedReward?.status === "credit"
                    ? t.revoke_disabled_credit_hint
                    : undefined
              }
              onClick={handleRevokeReward}
            >
              {revokeSubmitting ? t.revoking : t.revoke_reward}
            </button>
          </div>
          <button
            type="button"
            className="ref-reward-close-btn"
            onClick={() => setIsDetailsOpen(false)}
          >
            {t.modal_close}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default RewardsTab
